"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { Variants } from "motion/react";
import React, { useCallback, useRef, useState } from "react";
import { HiChevronDoubleLeft, HiChevronDoubleRight, HiOutlineFunnel, HiXMark } from "react-icons/hi2";
import { RiAlertLine, RiCheckLine, RiDoorOpenLine } from "react-icons/ri";
import PatientDisplay from "@/components/patient/patient-display";
import { Button, Checkbox, Label, Popover, PopoverContent, PopoverTrigger } from "@/components/ui";
import { ScrollShadow } from "@/components/ui/scroll-shadow";
import type { Group } from "@/components/ui/table";
import { GroupedTableBody, TableCell, TableHead, TableHeaderCell, TableRow } from "@/components/ui/table";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useModule } from "@/hooks/use-module";
import type { PatientStatusRow } from "@/hooks/use-patient-status";
import { usePatientStatus } from "@/hooks/use-patient-status";
import { useProviders } from "@/hooks/use-providers";
import { useLocationTimezoneOf } from "@/hooks/use-timezone";
import { formatBlockedByMessage } from "@/lib/checklist-dependencies";
import { calculateRelativeDuration, formatStaffName, formatTime } from "@/lib/format-data";
import { hasAppointmentChecklistPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { AppointmentChecklistType } from "@/types/enums/appointment";
import { serviceQueryOptions } from "@/api/provider/admin/service-query";
import { patientSidebarDefinition } from "@/features/sidebar/patient-sidebar";
import { useUpdateAppointmentChecklistMutation } from "@/query/check-in/calendar/event-query";
import { usePatientStatusDrawerStore } from "@/stores/patient-status-drawer-store";
import { isPatientStatusChecklistVisible, patientStatusVisibilityScope, usePatientStatusStore } from "@/stores/patient-status-store";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
type PatientStatusPanelProps = {
  className?: string;
  mode?: "desktop" | "mobile";
  onClose?: () => void;
};
const EmptyState: React.FC = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center justify-center py-6 text-body text-muted-foreground">No patients checked in.</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const PatientStatusPanel: React.FC<PatientStatusPanelProps> = ({
  className,
  mode = "desktop",
  onClose
}) => {
  const {
    groupIndex,
    rowsByGroup,
    isLoading,
    numEntries,
    availableChecklistNames,
    isOverdue
  } = usePatientStatus();
  const openSidebar = useRightSidebarStore(state => state.open);
  const {
    data: servicesById
  } = useQuery({
    ...serviceQueryOptions.list(),
    select: data => new Map(data?.map(service => [service.id, service]))
  });
  const completeChecklistMutation = useUpdateAppointmentChecklistMutation(servicesById);
  const {
    tenant
  } = useTenant();
  const visualDependenciesEnabled = useModule("CHECKLIST_DEPENDENCIES");
  const {
    currentUser
  } = useUser();
  const visibilityScope = patientStatusVisibilityScope(tenant?.subDomain, currentUser?.id);
  const {
    providerIdProviderMap
  } = useProviders();
  const locationTimezoneOf = useLocationTimezoneOf({
    viewerWhileLoading: true
  });
  const {
    isLocked,
    closeDrawer,
    lockDrawer
  } = usePatientStatusDrawerStore();
  const checklistVisibility = usePatientStatusStore(s => s.checklistVisibility);
  const checklistVisibilityDefaults = usePatientStatusStore(s_0 => s_0.checklistVisibilityDefaults);
  const setChecklistVisibility = usePatientStatusStore(s_1 => s_1.setChecklistVisibility);
  const setAllChecklistVisibility = usePatientStatusStore(s_2 => s_2.setAllChecklistVisibility);
  const [exitingRow, setExitingRow] = useState<PatientStatusRow | null>(null);
  const latestCompletionContext = useRef({
    rowsByGroup,
    currentUser,
    visualDependenciesEnabled
  });
  latestCompletionContext.current = {
    rowsByGroup,
    currentUser,
    visualDependenciesEnabled
  };
  const groupsForRender = groupIndex;
  const canCompletePatientStatusItem = useCallback((type: AppointmentChecklistType | null | undefined) => hasAppointmentChecklistPermission(currentUser, type), [currentUser]);
  const triggerCompletion = async (row: PatientStatusRow) => {
    if (!row.nextUpItem || !canCompletePatientStatusItem(row.nextUpItem.type)) return;
    if (visualDependenciesEnabled && row.nextUpBlockedBy?.length) return;
    const itemId = row.nextUpItem.id;
    setExitingRow(row);
    setTimeout(() => {
      setExitingRow(exiting => exiting?.id === row.id ? null : exiting);
      // An optimistic undo or realtime update can lock/remove this step
      // during the exit animation. Never complete the stale clicked row.
      const latest = latestCompletionContext.current;
      const latestRow = Object.values(latest.rowsByGroup).flat().find(candidate => candidate.id === row.id && candidate.event.id === row.event.id);
      if (!latestRow?.nextUpItem || latestRow.nextUpItem.id !== itemId || latest.visualDependenciesEnabled && latestRow.nextUpBlockedBy?.length || !hasAppointmentChecklistPermission(latest.currentUser, latestRow.nextUpItem.type)) return;
      completeChecklistMutation.mutateAsync({
        appointmentChecklistId: itemId,
        eventId: latestRow.event.id,
        completed: true,
        optimisticCompletedByUser: latest.currentUser || undefined,
        source: "patient_status_panel"
      });
    }, 350);
  };
  const renderTableHead = (_g: Group<PatientStatusRow>, _idx: number) => <TableHead>
      <TableRow className={cx("sticky z-[5] *:border-b-0 *:py-1 *:pt-3 *:text-caption *:text-foreground-secondary", "bg-background/40 backdrop-blur-lg")}>
        <TableHeaderCell className="w-40">Patient</TableHeaderCell>
        <TableHeaderCell className="w-3/12">Updated</TableHeaderCell>
        <TableHeaderCell className="w-6"></TableHeaderCell>
      </TableRow>
    </TableHead>;
  const handleCheckedChange = useCallback((checked: boolean, checklistName: string) => {
    if (visibilityScope) {
      setChecklistVisibility(visibilityScope, checklistName, checked);
    }
  }, [setChecklistVisibility, visibilityScope]);
  const rowVariants: Variants = {
    initial: {
      opacity: 0,
      x: -16
    },
    animate: {
      opacity: 1,
      x: 0
    },
    exit: {
      opacity: 0,
      x: 16
    }
  };
  const renderRow = (row_0: PatientStatusRow, _index?: number) => {
    const hasPermission = canCompletePatientStatusItem(row_0.nextUpItem?.type);
    const blockedBy = visualDependenciesEnabled ? row_0.nextUpBlockedBy ?? [] : [];
    const canCompleteItem = hasPermission && blockedBy.length === 0;
    const rowZone = locationTimezoneOf(row_0.event.locationId);
    return <TableRow key={row_0.id} className={cx("relative border-none", "duration-200 ease-in-out motion-duration-200", row_0.id === exitingRow?.id && "*:bg-status-success-bg")} variants={rowVariants} initial="initial" animate="animate" exit="exit" transition={{
      duration: 0.3
    }} onClick={() => openSidebar(patientSidebarDefinition, {
      patientTiny: row_0.patient,
      selectedEventId: row_0.event.id
    })}>
        <TableCell className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1 font-medium text-foreground">
            {isOverdue(row_0) && <RiAlertLine className={cx("h-3.5 w-3.5 flex-shrink-0 text-destructive", exitingRow?.id === row_0.id && "opacity-0")} />}
            <PatientDisplay patient={row_0.patient} />
          </div>
          {row_0.event.preferredProviderId && providerIdProviderMap.has(row_0.event.preferredProviderId) && <span className="truncate text-caption text-muted-foreground">
                Preferred:{" "}
                <span className="font-semibold">
                  {formatStaffName(row_0.event.preferredProviderId)}
                </span>
              </span>}
          <span className="truncate text-caption text-muted-foreground">
            {row_0.serviceTitle}
          </span>
          <span className="truncate text-caption text-muted-foreground">
            {formatTime(row_0.event.start, true, rowZone)} -{" "}
            {formatTime(row_0.event.end, true, rowZone)}
          </span>
        </TableCell>
        <TableCell className={cx("text-caption", isOverdue(row_0) && !(row_0.id === exitingRow?.id) && "text-destructive")}>
          {row_0.lastCheckedItem?.completedDate ? calculateRelativeDuration(new Date(row_0.lastCheckedItem.completedDate), false) : "—"}
        </TableCell>
        <TableCell>
          <Button variant="secondary" size="icon-sm" className={cx("h-4 w-4 rounded-full border p-0", "relative", "overflow-visible", !canCompleteItem && "cursor-not-allowed opacity-50", exitingRow?.id === row_0.id && "motion-preset-shake motion-preset-confetti !scale-125")} onClick={e => {
          e.stopPropagation();
          triggerCompletion(row_0);
        }} tooltip={!hasPermission ? "You do not have permission to complete this step" : blockedBy.length > 0 ? formatBlockedByMessage(blockedBy) : "Complete this step?"} icon={<RiCheckLine className={cx("text-status-success-text opacity-0", exitingRow?.id === row_0.id && "opacity-100")} />} />
        </TableCell>
      </TableRow>;
  };
  return <div className={cx("flex flex-col overflow-hidden", className)}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-3 h-10 md:shadow-none shadow-md">
        <div className="flex items-center gap-2">
          <RiDoorOpenLine className="h-4 w-4 text-foreground-secondary" />
          <span className="text-body font-semibold text-foreground">
            Patient Status
          </span>
        </div>
        <div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" className="text-muted-foreground hover:text-foreground !shadow-none size-9" tooltip="Filter" icon={HiOutlineFunnel} />
            </PopoverTrigger>
            <PopoverContent align="end" sideOffset={2}>
              <div className="flex flex-col gap-2">
                <div className="grid w-1/2 grid-cols-2 gap-1">
                  <Button variant="secondary" size="sm" className="shadow-none" onClick={() => visibilityScope && setAllChecklistVisibility(visibilityScope, true)}>
                    All
                  </Button>
                  <Button variant="secondary" size="sm" className="shadow-none" onClick={() => visibilityScope && setAllChecklistVisibility(visibilityScope, false)}>
                    None
                  </Button>
                </div>
                {availableChecklistNames.map(checklistName_0 => <div key={checklistName_0} className="flex items-center gap-2">
                    <Checkbox value={checklistName_0} checked={!visibilityScope || isPatientStatusChecklistVisible(checklistVisibility, checklistVisibilityDefaults, visibilityScope, checklistName_0)} id={`patient-status-${checklistName_0}`} onCheckedChange={(checked_0: boolean) => handleCheckedChange(checked_0, checklistName_0)} />
                    <Label htmlFor={`patient-status-${checklistName_0}`}>
                      {checklistName_0}
                    </Label>
                  </div>)}
              </div>
            </PopoverContent>
          </Popover>
          {mode === "desktop" && <Button variant="ghost" className="text-muted-foreground hover:text-foreground !shadow-none size-9" onClick={isLocked ? closeDrawer : lockDrawer} tooltip={isLocked ? "Close" : "Keep open"} icon={isLocked ? HiChevronDoubleLeft : HiChevronDoubleRight} />}
          {mode === "mobile" && onClose && <Button variant="ghost" className="text-muted-foreground hover:text-foreground !shadow-none size-9" onClick={onClose} tooltip="Close" icon={HiXMark} />}
        </div>
      </div>

      {/* Content */}
      {isLoading && !numEntries ? <div className="py-6 text-center text-body text-muted-foreground">
          Loading…
        </div> : !numEntries ? <EmptyState /> : <ScrollShadow.Root>
          <ScrollShadow.Viewport className="flex-1 overflow-y-auto !overflow-x-hidden">
            <GroupedTableBody groups={groupsForRender} colSpan={3} rowsByGroup={rowsByGroup} renderRow={renderRow} renderTableHead={renderTableHead} label="patients" size="sm" collapsible={false} tableClassName="!min-w-0" />
          </ScrollShadow.Viewport>
          <ScrollShadow.Top className="opacity-50" />
          <ScrollShadow.Bottom className="opacity-50" />
        </ScrollShadow.Root>}
    </div>;
};
export default PatientStatusPanel;